import { useEffect } from 'react'
import { create } from 'zustand'
import { api } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { Bot, User } from '../api/types'
import { findBot, readsEverything, readsEverythingWarning } from '../lib/bots'

interface BotsState {
  list: Bot[] | null
  error: string | null
  loading: Promise<Bot[] | null> | null
  /** GET /api/bots once per session; `force` refetches (e.g. when a picker opens). */
  load: (force?: boolean) => Promise<Bot[] | null>
  reset: () => void
}

export const useBotsStore = create<BotsState>((set, get) => ({
  list: null,
  error: null,
  loading: null,
  load: (force = false) => {
    const state = get()
    if (state.loading) return state.loading
    if (state.list && !force) return Promise.resolve(state.list)
    const loading = api
      .bots()
      .then((list) => {
        set({ list, error: null, loading: null })
        return list
      })
      .catch((error: unknown) => {
        set({ error: errorMessage(error), loading: null })
        return null
      })
    set({ loading })
    return loading
  },
  reset: () => set({ list: null, error: null, loading: null }),
}))

/** The bots that exist, loaded while `enabled`: names, handles, what they receive and where their messages go. */
export function useBots(enabled: boolean): { list: Bot[] | null; error: string | null } {
  const list = useBotsStore((s) => s.list)
  const error = useBotsStore((s) => s.error)
  useEffect(() => {
    if (enabled) void useBotsStore.getState().load()
  }, [enabled])
  return enabled ? { list, error } : { list: null, error: null }
}

/**
 * What to confirm before these bots join a conversation where each member reads along: one warning per bot that
 * receives every message. Waits for the bot list so a quick click can't skip the question, and warns about every
 * bot when the list can't be loaded.
 */
export async function everyMessageWarnings(bots: Pick<User, 'id' | 'displayName'>[]): Promise<string[]> {
  if (!bots.length) return []
  const list = await useBotsStore.getState().load()
  return bots.flatMap((user) => {
    const bot = findBot(list, user.id)
    if (bot) return readsEverything(bot) ? [readsEverythingWarning(bot)] : []
    return list ? [] : [`Couldn't check what ${user.displayName} receives. It may pass every message in this conversation to its service.`]
  })
}
